// **React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **Component Imports
import CustomDateTimePicker from '../../../../../components/date-time-picker'
import CustomDialog from '../../../../../components/dialog'
import QuotaTooltip from '../../../../../components/quota-tooltip'
import VirtualizedAutocomplete from '../../../../../components/virtualized-autocomplete'

// **MUI Imports
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from '@mui/material'

// ** API Imports
import {
  enableOrScheduleStageForAssessment,
  getFeatureQuota,
  getInterviewersOfStage
} from '../../../../../axios/api/contest'
import { getNonLearnerUsersOfWorkspace } from '../../../../../axios/api/workspace'

// **Third party Imports
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import * as yup from 'yup'

// **Utils Imports
import UserAvatar from '../../../../../components/user-avatar'
import { CHALLENGE_TYPES } from '../../../../../utils/constant'
import { showErrorToast } from '../../../../../utils/utils'

// component to enable or schedule stage for assessments (one candidate at a time)
const EnableOrScheduleStageModal = ({
  open,
  inputData,
  handleClose,
  title = null,
  refreshHandler = () => {},
  stageName = null,
  stageType = null,
  scheduleId = null,
  onSubmit,
  scheduleBtnText = 'Schedule',
  enableBtnText = 'Enable',
  isCustomLoading = false
}) => {
  // redux state
  const workspaceId = useSelector(
    (state) => state.workspace.selectedWorkspace?.id
  )

  // State
  const [loading, setLoading] = useState({
    enable: false,
    quota: false,
    users: false,
    interviewers: false
  })
  const [quotaDetails, setQuotaDetails] = useState(null)
  const [scheduleStage, setScheduleStage] = useState(false)
  const [userOptions, setUserOptions] = useState([])
  const [data, setData] = useState({
    startTime: ''
  })

  // variables
  const isInterviewStage = useMemo(() => {
    return stageType === CHALLENGE_TYPES.INTERVIEW
  }, [stageType])

  const schema = useMemo(() => {
    return yup.object().shape({
      interviewerIds: isInterviewStage
        ? yup
            .array()
            .of(yup.string())
            .min(1, 'Please select at least one interviewer')
        : yup.array().notRequired()
    })
  }, [isInterviewStage])

  // form Hook
  const {
    control,
    handleSubmit,
    clearErrors,
    setValue,
    trigger,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      interviewerIds: []
    },
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  // watched values
  const selectedInterviewer = watch('interviewerIds')

  // other variables
  const dropdownValue = useMemo(() => {
    const data = userOptions?.filter((opt) => {
      return selectedInterviewer?.includes(opt?.id)
    })
    return data
  }, [userOptions, selectedInterviewer])

  // Handlers
  const fetchFeatureQuota = useCallback(async (stageId, estimateCandidates) => {
    setLoading((prev) => ({ ...prev, quota: true }))
    const response = await getFeatureQuota(stageId, estimateCandidates)
    setLoading((prev) => ({ ...prev, quota: false }))

    if (response?.isError) {
      showErrorToast(response)
    } else {
      setQuotaDetails(response)
    }
  }, [])

  const fetchUserOptions = useCallback(async (workspaceId) => {
    setLoading((prev) => ({ ...prev, users: true }))
    const response = await getNonLearnerUsersOfWorkspace(workspaceId, false)
    setLoading((prev) => ({ ...prev, users: false }))

    if (response?.isError) {
      showErrorToast(response)
      setUserOptions([])
    } else {
      setUserOptions(response)
    }
  }, [])

  const fetchInterviewers = useCallback(
    async (scheduleId) => {
      setLoading((prev) => ({ ...prev, interviewer: true }))
      const response = await getInterviewersOfStage(scheduleId)
      setLoading((prev) => ({ ...prev, interviewer: false }))

      if (response?.isError) {
        showErrorToast(response)
        setValue('interviewerIds', [])
      } else {
        setValue('interviewerIds', response)
      }
    },
    [setValue]
  )

  const handleDateChange = useCallback((value) => {
    setData((prev) => ({
      ...prev,
      startTime: value ? new Date(value).toISOString() : value
    }))
  }, [])

  const handleEnableStage = useCallback(
    async (formData) => {
      setLoading((prev) => ({ ...prev, enable: true }))
      const payload = { ...data }
      if (!scheduleStage) {
        payload.startTime = null
      }
      if (isInterviewStage) {
        payload.interviewerIds = formData?.interviewerIds
      }

      if (onSubmit && typeof onSubmit === 'function') {
        await onSubmit(payload)
        setLoading((prev) => ({ ...prev, enable: false }))
        return
      }

      const response = await enableOrScheduleStageForAssessment(payload)
      setLoading((prev) => ({ ...prev, enable: false }))

      if (response?.isError) {
        showErrorToast(response)
      } else {
        if (payload?.startTime) {
          toast.success('Stage scheduled for selected candidate(s)')
        } else {
          toast.success('Stage Enabled for selected candidate(s)')
        }
        refreshHandler(true)
        handleClose()
      }
    },
    [
      data,
      refreshHandler,
      handleClose,
      scheduleStage,
      isInterviewStage,
      onSubmit
    ]
  )

  // useEffects
  useEffect(() => {
    if (!open) {
      setData({ candidateId: null, startTime: '' })
      setScheduleStage(false)
      setQuotaDetails(null)
    } else {
      setData((prev) => ({
        ...prev,
        candidateId: inputData?.candidateId || prev.candidateId,
        contestStageId: inputData?.contestStageId || prev.contestStageId,
        startTime: inputData?.startTime || prev.startTime || null
      }))

      setScheduleStage(!!inputData?.startTime)

      if (inputData?.contestStageId) {
        fetchFeatureQuota(inputData.contestStageId, 1) // 1 because enabling only one candidate at a time in assessments
      }
    }
  }, [inputData, open, fetchFeatureQuota])

  useEffect(() => {
    if (workspaceId && isInterviewStage && open) {
      fetchUserOptions(workspaceId)
    }
  }, [isInterviewStage, workspaceId, fetchUserOptions, open])

  useEffect(() => {
    if (isInterviewStage && scheduleId && open) {
      fetchInterviewers(scheduleId)
    }
  }, [isInterviewStage, scheduleId, fetchInterviewers, open])

  useEffect(() => {
    if (!open) {
      setLoading({
        enable: false,
        quota: false,
        users: false,
        interviewers: false
      })
      reset()
      clearErrors()
      setUserOptions([])
    }
  }, [open, reset, clearErrors])

  return (
    <CustomDialog
      open={open}
      outsideClikClose={false}
      handleClose={handleClose}
      title={title || `Enable or Schedule ${stageName}`}
      renderHeader={() => (
        <QuotaTooltip data={quotaDetails} loading={loading?.quota} />
      )}
      dialogPaperStyle={{
        maxWidth: '550px',
        width: '40%',
        '& .MuiDialogContent-root': {
          padding: '0px !important'
        }
      }}
    >
      <Grid container className="enable-or-schedule-stage-main-grid-container">
        <Grid item xs={12} container padding={'10px 20px'} spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <RadioGroup
                onChange={(e) => {
                  const value = e.target.value === 'true'
                  setScheduleStage(value)
                }}
                row
                value={scheduleStage}
              >
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="Enable now"
                />
                <FormControlLabel
                  sx={{ flex: 'unset' }}
                  value={true}
                  control={<Radio />}
                  label="Schedule for later"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {scheduleStage && (
            <Grid item xs={12}>
              <CustomDateTimePicker
                label="Stage Start Time"
                value={data?.startTime}
                onChange={handleDateChange}
                usedInHookForm={false}
                timeIntervals={5}
                required
              />
            </Grid>
          )}

          {/* {loading?.quota ? (
            <Grid item xs={12}>
              <Skeleton height={40} width={'100%'} animation="wave" />
            </Grid>
          ) : (
            <Grid item xs={12}>
              <QuotaWidget data={quotaDetails} />
            </Grid>
          )} */}

          {isInterviewStage && (
            <Grid item xs={12} mt={2}>
              <VirtualizedAutocomplete
                name="interviewerIds"
                control={control}
                label={'Assign Interviewers'}
                options={userOptions}
                needVirtualization={false}
                helperText={
                  errors?.interviewerIds?.message ||
                  'Please select at least one interviewer'
                }
                error={errors?.interviewerIds}
                autoCompleteProps={{
                  multiple: true,
                  options: userOptions,
                  disableCloseOnSelect: true,
                  disabled: loading?.users,
                  value: dropdownValue,
                  onChange: (_, newValue) => {
                    const value = newValue.map((item) => item.id)
                    setValue('interviewerIds', value || [])
                    trigger('interviewerIds')
                  },
                  getOptionLabel: (option) => option?.email || '',
                  renderOption: (props, option) => {
                    // variables
                    const { key, ...optionProps } = props

                    return (
                      <li key={key} {...optionProps} value={option}>
                        <UserAvatar
                          data={{
                            name: option?.name,
                            email: option?.email
                          }}
                        />
                      </li>
                    )
                  }
                }}
              />
            </Grid>
          )}
        </Grid>

        <Grid item xs={12} padding={'20px'}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '16px'
            }}
          >
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(handleEnableStage)}
              variant="contained"
              disabled={(scheduleStage && !data.startTime) || loading?.enable}
            >
              {loading?.enable && !isCustomLoading ? (
                <CircularProgress size={22} />
              ) : scheduleStage ? (
                scheduleBtnText
              ) : (
                enableBtnText
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </CustomDialog>
  )
}

export default memo(EnableOrScheduleStageModal)
